import { DaoType, DtoType } from '../mapper';
import { DtoMapper } from '../mapper/dto-mapper';
import { PagedData } from '../paged-data';
import { createSearchableSortableHttpParam } from '../searchable-sortable-http-param';
import { UUID } from '../uuid';
import { CrudGetAllDto } from './crud-get-all-dto';
import { CrudRepository } from './crud.repository';

export abstract class CrudService<Dao extends DaoType, Dto extends DtoType> {
  protected constructor(
    protected readonly repository: CrudRepository<Dao>,
    protected readonly dtoMapper: DtoMapper<Dao, Dto>,
  ) {}

  async create(resourceDto: Dto): Promise<Dto | null> {
    const resource: Dao = this.dtoMapper.sync.toDao(resourceDto);
    return this.dtoMapper.fromDao(this.repository.create({ resource }));
  }

  async getById(id: UUID): Promise<Dto | null> {
    return this.dtoMapper.fromDao(this.repository.get({ id }));
  }

  async getAll(dto: CrudGetAllDto): Promise<PagedData<Dto>> {
    const { query, pageable } = createSearchableSortableHttpParam(dto);
    return this.dtoMapper.fromPagedDao(this.repository.getAll({ query, pageable }));
  }

  async update(resource: Dao): Promise<Dto | null> {
    return this.dtoMapper.fromDao(this.repository.update({ resource }));
  }

  async patch(resource: Dao): Promise<Dto | null> {
    return this.dtoMapper.fromDao(this.repository.patch({ resource }));
  }

  async delete(id: UUID): Promise<UUID | null> {
    return this.repository.delete({ id });
  }

  async deleteAll(ids: UUID[]): Promise<UUID[] | null> {
    return this.repository.deleteAll({ ids });
  }

  async getByIds(ids: UUID[]): Promise<Awaited<Dto | null>[]> {
    return Promise.all(ids.map((id: UUID) => this.getById(id)));
  }
}
